import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { FiMapPin, FiPlus, FiTrash2 } from "react-icons/fi";
import VmsPageCard from "../components/VmsPageCard";
import { VMS_COLORS } from "../constants/theme";
import { vmsApi } from "../services/vmsApi";

const VmsGates = () => {
  const [gates, setGates] = useState([]);
  const [newGateName, setNewGateName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingGateId, setDeletingGateId] = useState("");
  const toast = useToast();

  const loadGates = async () => {
    setIsLoading(true);
    try {
      const data = await vmsApi.getGates();
      setGates(Array.isArray(data) ? data : []);
    } catch (err) {
      toast({ title: "Failed to load gates", description: err?.message, status: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadGates().catch(() => {});
  }, []);

  const handleCreateGate = async (e) => {
    e.preventDefault();
    if (!newGateName.trim()) {
      toast({ title: "Gate name required", status: "warning" });
      return;
    }
    setIsCreating(true);
    try {
      await vmsApi.createGate({ name: newGateName.trim() });
      setNewGateName("");
      await loadGates();
      toast({ title: "Gate created", status: "success" });
    } catch (err) {
      toast({ title: "Create failed", description: err?.message, status: "error" });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteGate = async (gate) => {
    const gateId = gate?.gate_id || gate?.id;
    if (!gateId) return;
    const confirmed = window.confirm(`Delete gate "${gate?.name || gateId}"?`);
    if (!confirmed) return;
    setDeletingGateId(gateId);
    try {
      await vmsApi.deleteGate(gateId);
      await loadGates();
      toast({ title: "Gate deleted", status: "success" });
    } catch (err) {
      toast({ title: "Delete failed", description: err?.message, status: "error" });
    } finally {
      setDeletingGateId("");
    }
  };

  return (
    <VStack align="stretch" spacing={5}>
      <VmsPageCard>
        <HStack justify="space-between" mb={4}>
          <Box>
            <Text fontSize="xl" fontWeight="800" color="gray.800">
              Gates
            </Text>
            <Text color="gray.500" fontSize="sm">
              Manage entry gates available for visitor sign-in.
            </Text>
          </Box>
          <Badge colorScheme="green" px={3} py={1} borderRadius="full">
            {gates.length} gate{gates.length === 1 ? "" : "s"}
          </Badge>
        </HStack>

        <Box
          as="form"
          onSubmit={handleCreateGate}
          mb={5}
          p={4}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="12px"
          bg="gray.50"
        >
          <HStack align="end" spacing={3} flexWrap="wrap">
            <FormControl maxW={{ base: "100%", md: "360px" }} isRequired>
              <FormLabel mb={1}>Gate Name</FormLabel>
              <Input
                placeholder="e.g. Main Gate"
                value={newGateName}
                onChange={(e) => setNewGateName(e.target.value)}
                bg="white"
              />
            </FormControl>
            <Button
              type="submit"
              leftIcon={<FiPlus />}
              bg={VMS_COLORS.primary}
              color="white"
              _hover={{ bg: VMS_COLORS.primaryDark }}
              isLoading={isCreating}
            >
              Add Gate
            </Button>
          </HStack>
        </Box>

        <TableContainer border="1px solid" borderColor="gray.200" borderRadius="14px" overflow="hidden">
          <Table size="md" variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Gate Name</Th>
                <Th>Gate ID</Th>
                <Th textAlign="right">Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {gates.map((gate) => {
                const gateId = gate?.gate_id || gate?.id;
                return (
                  <Tr key={gateId || gate?.name} _hover={{ bg: "gray.50" }}>
                    <Td>
                      <HStack spacing={2}>
                        <Box as={FiMapPin} color={VMS_COLORS.primary} />
                        <Text fontWeight="600">{gate?.name || "-"}</Text>
                      </HStack>
                    </Td>
                    <Td color="gray.600" fontSize="sm">
                      {gateId || "-"}
                    </Td>
                    <Td textAlign="right">
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme="red"
                        leftIcon={<FiTrash2 />}
                        onClick={() => handleDeleteGate(gate)}
                        isLoading={deletingGateId === gateId}
                      >
                        Delete
                      </Button>
                    </Td>
                  </Tr>
                );
              })}

              {!isLoading && !gates.length ? (
                <Tr>
                  <Td colSpan={3}>
                    <Text py={10} textAlign="center" color="gray.500">
                      No gates configured yet. Add your first gate above.
                    </Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </TableContainer>
      </VmsPageCard>
    </VStack>
  );
};

export default VmsGates;
